import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import toast from 'react-hot-toast';

interface LoyaltyConfig {
 // Master Toggle for Points System
 pointsSystemEnabled: boolean; // Master toggle to enable/disable entire points system

 // Welcome Configuration
 welcomeMessage: string; // Custom welcome message for new users
 welcomeStampEnabled: boolean; // Give a gift stamp to new users
 welcomeStampCount: number; // Number of gift stamps (default: 1)

 // Points System
 pointsPerService: {
 mode: 'fixed' | 'per_service';
 fixedValue?: number;
 perServiceValues?: Record<string, number>;
 };
 welcomePoints: number; // Points given to new users on registration
 firstVisitBonus: number;
 noShowPenalty: number;
 lateCancelPenalty: number;
 cancelGracePeriodMinutes: number;

 // VIP System
 vipThreshold: number;
 vipRequiresApproval: boolean;

 // Stamps System
 pointsPerStamp: number;
 pointsToStampsEnabled: boolean; // Permite a clientes canjear puntos por sellos
 stampsPerCard: number;
 maxActiveCards: number;

 // Blocking
 blockThreshold: number; // Negative points that trigger block

 // Redemption
 eligibleRedemptionServices: string[];
 basicServicePrice: number;
 rewardDiscountType: 'fixed' | 'percentage'; // Tipo de descuento: valor fijo o porcentaje
 rewardValueEuros: number; // Reward value in euros (displayed to admin, stored as cents)
 rewardPercentage: number; // Porcentaje de descuento (1-100)
}

const DEFAULT_CONFIG: LoyaltyConfig = {
 pointsSystemEnabled: false, // Disabled by default as requested
 welcomeMessage: '¡Bienvenido a Monkey Barber Club! Gracias por unirte a nuestra familia.', // Default welcome message
 welcomeStampEnabled: true, // Give gift stamp to new users by default
 welcomeStampCount: 1, // 1 gift stamp by default
 pointsPerService: {
 mode: 'fixed',
 fixedValue: 10
 },
 welcomePoints: 5, // Default welcome points for new users
 firstVisitBonus: 50,
 noShowPenalty: 50,
 lateCancelPenalty: 25,
 cancelGracePeriodMinutes: 60,
 vipThreshold: 500,
 vipRequiresApproval: true,
 pointsPerStamp: 100,
 pointsToStampsEnabled: true,
 stampsPerCard: 10,
 maxActiveCards: 3,
 blockThreshold: -100,
 eligibleRedemptionServices: [],
 basicServicePrice: 0,
 rewardDiscountType: 'fixed', // Default: valor fijo
 rewardValueEuros: 10, // Default 10€
 rewardPercentage: 50, // Default 50%
};

interface LoyaltyConfigPanelProps {
 selectedFranchiseId?: string;
}

// Default franchise ID for Monkey Barbershop
const DEFAULT_FRANCHISE_ID = 'lILDGxMgfsifzpqg4YZu';

export const LoyaltyConfigPanel: React.FC<LoyaltyConfigPanelProps> = ({ selectedFranchiseId }) => {
 const [config, setConfig] = useState<LoyaltyConfig>(DEFAULT_CONFIG);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [hasChanges, setHasChanges] = useState(false);
 const [currentFranchiseId, setCurrentFranchiseId] = useState<string | null>(null);

 // Determine which franchise to use - default to main franchise if 'all' or not specified
 const effectiveFranchiseId = selectedFranchiseId && selectedFranchiseId !== 'all'
 ? selectedFranchiseId
 : (currentFranchiseId || DEFAULT_FRANCHISE_ID);

 useEffect(() => {
 loadConfig();
 }, [effectiveFranchiseId]);

 const loadConfig = async () => {
 try {
 setLoading(true);

 // First, try to load from loyalty_configs/{franchiseId} if we have a franchise
 if (effectiveFranchiseId) {
 const franchiseConfigDoc = await getDoc(doc(db, 'loyalty_configs', effectiveFranchiseId));

 if (franchiseConfigDoc.exists()) {
 const data = franchiseConfigDoc.data();
 // Map the Firestore structure to our local config structure
 setConfig({
 ...DEFAULT_CONFIG,
 pointsSystemEnabled: data.pointsSystemEnabled ?? false,
 welcomeMessage: data.welcomeMessage ?? DEFAULT_CONFIG.welcomeMessage,
 welcomeStampEnabled: data.welcomeStampEnabled ?? true,
 welcomeStampCount: data.welcomeStampCount ?? 1,
 pointsPerService: {
 mode: data.earnRules?.mode || 'fixed',
 fixedValue: data.earnRules?.fixedAmount || 10
 },
 welcomePoints: data.welcomePoints ?? 5,
 firstVisitBonus: data.bonuses?.firstVisit || 50,
 noShowPenalty: data.penalties?.noShow || 50,
 lateCancelPenalty: data.penalties?.lateCancellation?.amount || 25,
 cancelGracePeriodMinutes: data.penalties?.lateCancellation?.gracePeriodMinutes || 60,
 vipThreshold: data.vip?.pointsRequired || 500,
 vipRequiresApproval: data.vip?.requiresApproval ?? true,
 pointsPerStamp: data.stamps?.pointsConversion?.pointsPerStamp || data.pointsPerStamp || 100,
 pointsToStampsEnabled: data.stamps?.pointsConversion?.enabled ?? data.pointsToStampsEnabled ?? true,
 stampsPerCard: data.stamps?.stampsPerCard || 10,
 maxActiveCards: data.stamps?.maxActiveCards || 3,
 blockThreshold: data.blocking?.threshold || -100,
 eligibleRedemptionServices: [],
 basicServicePrice: 0,
 rewardDiscountType: data.stamps?.rewardDiscountType || 'fixed',
 rewardValueEuros: (data.stamps?.rewardValueCents || 1000) / 100,
 rewardPercentage: data.stamps?.rewardPercentage || 50,
 });
 setCurrentFranchiseId(effectiveFranchiseId);
 setLoading(false);
 return;
 }
 }

 // Fallback: load from legacy config/loyalty
 const legacyConfigDoc = await getDoc(doc(db, 'config', 'loyalty'));
 if (legacyConfigDoc.exists()) {
 setConfig({ ...DEFAULT_CONFIG, ...legacyConfigDoc.data() as LoyaltyConfig });
 }

 setLoading(false);
 } catch (error) {
 console.error('Error loading loyalty config:', error);
 toast.error('Error al cargar configuración');
 setLoading(false);
 }
 };

 const handleSave = async () => {
 if (!effectiveFranchiseId) {
 toast.error('Por favor selecciona una franquicia primero');
 return;
 }

 setSaving(true);

 try {
 // Save to loyalty_configs/{franchiseId} with the correct structure
 const firestoreConfig = {
 franchiseId: effectiveFranchiseId,
 enabled: true,
 pointsSystemEnabled: config.pointsSystemEnabled, // Master toggle for points system
 welcomeMessage: config.welcomeMessage, // Custom welcome message
 welcomeStampEnabled: config.welcomeStampEnabled, // Give gift stamp to new users
 welcomeStampCount: config.welcomeStampCount, // Number of gift stamps
 welcomePoints: config.welcomePoints, // Points for new users
 earnRules: {
 mode: config.pointsPerService.mode,
 fixedAmount: config.pointsPerService.fixedValue || 10,
 defaultRate: config.pointsPerService.fixedValue || 10
 },
 bonuses: {
 firstVisit: config.firstVisitBonus,
 birthdayMonth: 30,
 referral: 25,
 specialEvents: {}
 },
 penalties: {
 noShow: -Math.abs(config.noShowPenalty),
 lateCancellation: {
 amount: -Math.abs(config.lateCancelPenalty),
 gracePeriodMinutes: config.cancelGracePeriodMinutes
 },
 inappropriateBehavior: -100
 },
 vip: {
 enabled: true,
 pointsRequired: config.vipThreshold,
 requiresApproval: config.vipRequiresApproval,
 autoRevoke: true
 },
 blocking: {
 enabled: true,
 threshold: config.blockThreshold,
 autoBlock: true
 },
 stamps: {
 enabled: true,
 stampsPerCard: config.stampsPerCard,
 maxActiveCards: config.maxActiveCards,
 rewardDiscountType: config.rewardDiscountType, // 'fixed' or 'percentage'
 rewardValueCents: Math.round(config.rewardValueEuros * 100), // Store in cents
 rewardPercentage: config.rewardPercentage, // Percentage value (1-100)
 autoStampPerService: true,
 pointsConversion: {
 enabled: config.pointsToStampsEnabled,
 pointsPerStamp: config.pointsPerStamp,
 minimumPoints: config.pointsPerStamp,
 maxStampsPerDay: 50,
 requiresBarberApproval: true
 }
 },
 pointsToStampsEnabled: config.pointsToStampsEnabled,
 pointsPerStamp: config.pointsPerStamp,
 updatedAt: new Date()
 };

 await setDoc(doc(db, 'loyalty_configs', effectiveFranchiseId), firestoreConfig, { merge: true });

 // Also save to legacy location for backwards compatibility
 await setDoc(doc(db, 'config', 'loyalty'), config);

 toast.success('Configuración guardada exitosamente');
 setHasChanges(false);
 } catch (error) {
 console.error('Error saving config:', error);
 toast.error('Error al guardar configuración');
 } finally {
 setSaving(false);
 }
 };

 const updateConfig = <K extends keyof LoyaltyConfig>(
 key: K,
 value: LoyaltyConfig[K]
 ) => {
 setConfig(prev => ({ ...prev, [key]: value }));
 setHasChanges(true);
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center p-8">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent/30"></div>
 </div>
 );
 }

 return (
 <div className="max-w-4xl mx-auto p-6">
 <div className="bg-white rounded-lg shadow-lg bg-surface-2">
 {/* Header */}
 <div className="border-b border-hairline p-6 border-hairline">
 <h2 className="text-2xl font-bold text-ink">
 Configuración de Lealtad
 </h2>
 <p className="mt-2 text-sm text-ink-muted">
 Gestiona el sistema de puntos, sellos y estatus VIP
 </p>
 </div>

 {/* Content */}
 <div className="p-6 space-y-8">

 {/* Master Toggle - Sistema de Puntos */}
 <section className="bg-danger/10 border-2 border-danger/30 rounded-lg p-4">
 <div className="flex items-center justify-between">
 <div className="flex-1">
 <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
 🎚️ Sistema de Puntos
 <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
 config.pointsSystemEnabled
 ? 'bg-success/10 text-success'
 : 'bg-danger/10 text-danger'
 }`}>
 {config.pointsSystemEnabled ? 'ACTIVO' : 'DESACTIVADO'}
 </span>
 </h3>
 <p className="mt-1 text-sm text-ink-muted">
 {config.pointsSystemEnabled
 ? 'El sistema de puntos está activo. Los clientes pueden ver y acumular puntos.'
 : 'El sistema de puntos está desactivado. Los clientes no verán información de puntos.'}
 </p>
 </div>
 <label className="relative inline-flex items-center cursor-pointer ml-4">
 <input
 type="checkbox"
 checked={config.pointsSystemEnabled}
 onChange={(e) => updateConfig('pointsSystemEnabled', e.target.checked)}
 className="sr-only peer"
 />
 <div className="w-14 h-7 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent dark:peer-focus:ring-accent rounded-full peer bg-surface-2 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-hairline after:border after:rounded-full after:h-6 after:w-6 after:transition-all border-hairline peer-checked:bg-accent"></div>
 </label>
 </div>
 </section>

 {/* Welcome Configuration - Always visible */}
 <section className="bg-info/10 border border-info/30 rounded-lg p-4">
 <h3 className="text-lg font-semibold text-ink mb-4">
 👋 Configuración de Bienvenida
 </h3>
 <p className="text-sm text-ink-muted mb-4">
 Personaliza el mensaje y regalos de bienvenida para nuevos usuarios
 </p>

 <div className="space-y-4">
 {/* Mensaje de bienvenida */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 📝 Mensaje de bienvenida
 </label>
 <textarea
 value={config.welcomeMessage}
 onChange={(e) => updateConfig('welcomeMessage', e.target.value)}
 rows={3}
 maxLength={500}
 placeholder="Escribe un mensaje de bienvenida personalizado..."
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle resize-none"
 />
 <p className="mt-1 text-xs text-ink-subtle">
 {config.welcomeMessage.length}/500 caracteres
 </p>
 </div>

 {/* Sello de regalo */}
 <div className="flex items-start gap-4 p-4 bg-surface rounded-lg border border-hairline">
 <div className="flex-1">
 <label className="flex items-center cursor-pointer">
 <input
 type="checkbox"
 checked={config.welcomeStampEnabled}
 onChange={(e) => updateConfig('welcomeStampEnabled', e.target.checked)}
 className="mr-3 w-5 h-5 text-info rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <div>
 <span className="block text-sm font-medium text-ink">
 🎁 Sello de regalo de bienvenida
 </span>
 <p className="text-xs text-ink-muted mt-1">
 Regala sellos a nuevos usuarios al registrarse para empezar con ventaja
 </p>
 </div>
 </label>
 </div>

 {config.welcomeStampEnabled && (
 <div className="flex items-center gap-2">
 <input
 type="number"
 value={config.welcomeStampCount}
 onChange={(e) => updateConfig('welcomeStampCount', Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
 min="1"
 max="5"
 className="w-16 px-2 py-1 border border-hairline rounded bg-white bg-surface-2 text-ink text-center"
 />
 <span className="text-sm text-ink-muted">sello(s)</span>
 </div>
 )}
 </div>

 {/* Vista previa */}
 <div className="p-4 bg-surface-2 rounded-lg border border-accent/30">
 <p className="text-xs font-semibold text-accent mb-2">
 👁️ Vista previa del mensaje de bienvenida:
 </p>
 <div className="bg-surface p-3 rounded-lg shadow-sm">
 <p className="text-sm text-ink-muted italic">
"{config.welcomeMessage}"
 </p>
 {config.welcomeStampEnabled && (
 <p className="mt-2 text-sm font-semibold text-success">
 🎁 +{config.welcomeStampCount} sello{config.welcomeStampCount > 1 ? 's' : ''} de regalo
 </p>
 )}
 </div>
 </div>
 </div>
 </section>

 {/* Sistema de Puntos - Detalles (only show if enabled) */}
 {config.pointsSystemEnabled && (
 <section>
 <h3 className="text-lg font-semibold text-ink mb-4">
 💰 Configuración de Puntos
 </h3>

 <div className="space-y-4">
 {/* Puntos por servicio */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Puntos por servicio
 </label>
 <div className="flex gap-4">
 <label className="flex items-center">
 <input
 type="radio"
 checked={config.pointsPerService.mode === 'fixed'}
 onChange={() => updateConfig('pointsPerService', {
 mode: 'fixed',
 fixedValue: 10
 })}
 className="mr-2 text-accent focus:ring-accent bg-surface-2 border-hairline"
 />
 <span className="text-sm text-ink-muted">Fijo para todos</span>
 </label>
 <label className="flex items-center">
 <input
 type="radio"
 checked={config.pointsPerService.mode === 'per_service'}
 onChange={() => updateConfig('pointsPerService', {
 mode: 'per_service',
 perServiceValues: {}
 })}
 className="mr-2 text-accent focus:ring-accent bg-surface-2 border-hairline"
 />
 <span className="text-sm text-ink-muted">Por servicio</span>
 </label>
 </div>

 {config.pointsPerService.mode === 'fixed' && (
 <div className="mt-2">
 <input
 type="number"
 value={config.pointsPerService.fixedValue || 10}
 onChange={(e) => {
 const val = parseInt(e.target.value) || 0;
 updateConfig('pointsPerService', {
 mode: 'fixed',
 fixedValue: val
 });
 }}
 min="1"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos</span>
 </div>
 )}
 </div>

 {/* Puntos de bienvenida */}
 <div className="bg-success/10 border border-success/30 rounded-lg p-4">
 <label className="block text-sm font-medium text-ink-muted mb-2">
 🎁 Puntos de bienvenida (nuevos usuarios)
 </label>
 <div className="flex items-center gap-3">
 <input
 type="number"
 value={config.welcomePoints}
 onChange={(e) => updateConfig('welcomePoints', parseInt(e.target.value) || 0)}
 min="0"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="text-sm text-ink-muted">puntos</span>
 </div>
 <p className="mt-2 text-xs text-ink-subtle">
 Puntos que recibe un usuario al registrarse por primera vez. Se mostrará un modal de bienvenida con los puntos ganados.
 </p>
 </div>

 {/* Bonus primera visita */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Bonus primera visita
 </label>
 <input
 type="number"
 value={config.firstVisitBonus}
 onChange={(e) => updateConfig('firstVisitBonus', parseInt(e.target.value) || 0)}
 min="0"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos</span>
 </div>

 {/* Penalización no-show */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Penalización por no presentarse
 </label>
 <input
 type="number"
 value={config.noShowPenalty}
 onChange={(e) => updateConfig('noShowPenalty', parseInt(e.target.value) || 0)}
 min="0"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos negativos</span>
 </div>

 {/* Penalización cancelación tardía */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Penalización por cancelación tardía
 </label>
 <input
 type="number"
 value={config.lateCancelPenalty}
 onChange={(e) => updateConfig('lateCancelPenalty', parseInt(e.target.value) || 0)}
 min="0"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos negativos</span>
 </div>

 {/* Período de gracia para cancelar */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Período de gracia para cancelar sin penalización
 </label>
 <input
 type="number"
 value={config.cancelGracePeriodMinutes}
 onChange={(e) => updateConfig('cancelGracePeriodMinutes', parseInt(e.target.value) || 0)}
 min="0"
 max="1440"
 step="15"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">minutos antes</span>
 </div>

 {/* Umbral de bloqueo */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Bloquear cuenta cuando llegue a
 </label>
 <input
 type="number"
 value={config.blockThreshold}
 onChange={(e) => updateConfig('blockThreshold', parseInt(e.target.value) || 0)}
 max="0"
 min="-1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos negativos</span>
 <p className="mt-1 text-xs text-ink-subtle">
 Ej: -100 bloqueará usuarios que tengan -100 puntos o menos
 </p>
 </div>
 </div>
 </section>
 )}

 {/* Sistema VIP - Also depends on points system */}
 {config.pointsSystemEnabled && (
 <section className="border-t border-hairline pt-6 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">
 👑 Sistema VIP
 </h3>

 <div className="space-y-4">
 {/* Umbral VIP */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Puntos necesarios para ser VIP
 </label>
 <input
 type="number"
 value={config.vipThreshold}
 onChange={(e) => updateConfig('vipThreshold', parseInt(e.target.value) || 0)}
 min="0"
 max="10000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos</span>
 </div>

 {/* Requiere aprobación */}
 <div className="flex items-center">
 <input
 type="checkbox"
 checked={config.vipRequiresApproval}
 onChange={(e) => updateConfig('vipRequiresApproval', e.target.checked)}
 className="h-4 w-4 text-accent rounded focus:ring-accent bg-surface-2 border-hairline"
 />
 <label className="ml-2 text-sm text-ink-muted">
 Requiere aprobación manual del administrador
 </label>
 </div>
 </div>
 </section>
 )}

 {/* Sistema de Sellos - Always visible */}
 <section className="border-t border-hairline pt-6 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">
 🎫 Sistema de Sellos
 </h3>

 <div className="space-y-4">
 {/* Conversión puntos a sellos - only if points system enabled */}
 {config.pointsSystemEnabled && (
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Puntos necesarios por 1 sello
 </label>
 <input
 type="number"
 value={config.pointsPerStamp}
 onChange={(e) => updateConfig('pointsPerStamp', parseInt(e.target.value) || 0)}
 min="1"
 max="1000"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">puntos</span>
 </div>
 )}

 {/* Habilitar canje de puntos por sellos - only if points system enabled */}
 {config.pointsSystemEnabled && (
 <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
 <label className="flex items-start cursor-pointer">
 <input
 type="checkbox"
 checked={config.pointsToStampsEnabled || false}
 onChange={(e) => updateConfig('pointsToStampsEnabled', e.target.checked)}
 className="mt-1 mr-3 w-5 h-5 text-accent rounded focus:ring-accent bg-surface-2 border-hairline"
 />
 <div className="flex-1">
 <span className="block text-sm font-medium text-ink mb-1">
 Permitir canje de puntos por sellos
 </span>
 <p className="text-xs text-ink-muted">
 Si está habilitado, los clientes podrán canjear {config.pointsPerStamp} puntos por 1 sello adicional desde la página de fidelización.
 Esto les da más flexibilidad para obtener recompensas más rápido.
 </p>
 </div>
 </label>
 </div>
 )}

 {/* Sellos por tarjeta */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Sellos necesarios para completar tarjeta
 </label>
 <input
 type="number"
 value={config.stampsPerCard}
 onChange={(e) => updateConfig('stampsPerCard', parseInt(e.target.value) || 0)}
 min="1"
 max="20"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">sellos</span>
 </div>

 {/* Máximo de tarjetas activas */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Máximo de tarjetas activas simultáneas
 </label>
 <input
 type="number"
 value={config.maxActiveCards}
 onChange={(e) => updateConfig('maxActiveCards', parseInt(e.target.value) || 0)}
 min="1"
 max="10"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">tarjetas</span>
 </div>

 {/* Tipo de descuento de la recompensa */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Tipo de descuento al completar tarjeta
 </label>
 <div className="flex gap-4 mb-3">
 <label className="flex items-center">
 <input
 type="radio"
 checked={config.rewardDiscountType === 'fixed'}
 onChange={() => updateConfig('rewardDiscountType', 'fixed')}
 className="mr-2 text-accent focus:ring-accent bg-surface-2 border-hairline"
 />
 <span className="text-sm text-ink-muted">Valor fijo (€)</span>
 </label>
 <label className="flex items-center">
 <input
 type="radio"
 checked={config.rewardDiscountType === 'percentage'}
 onChange={() => updateConfig('rewardDiscountType', 'percentage')}
 className="mr-2 text-accent focus:ring-accent bg-surface-2 border-hairline"
 />
 <span className="text-sm text-ink-muted">Porcentaje (%)</span>
 </label>
 </div>

 {config.rewardDiscountType === 'fixed' ? (
 <div>
 <div className="flex items-center gap-2">
 <input
 type="number"
 value={config.rewardValueEuros}
 onChange={(e) => updateConfig('rewardValueEuros', parseFloat(e.target.value) || 10)}
 min="1"
 max="100"
 step="0.5"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="text-sm text-ink-muted">€</span>
 </div>
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">
 Valor fijo del vale de descuento. Ej: 10€ de descuento en el siguiente servicio.
 </p>
 </div>
 ) : (
 <div>
 <div className="flex items-center gap-2">
 <input
 type="number"
 value={config.rewardPercentage}
 onChange={(e) => updateConfig('rewardPercentage', Math.max(1, Math.min(100, parseInt(e.target.value) || 50)))}
 min="1"
 max="100"
 step="5"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="text-sm text-ink-muted">%</span>
 </div>
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">
 Porcentaje de descuento sobre el precio del servicio. Ej: 50% = si el corte vale 15€, el cliente paga 7.50€.
 </p>
 </div>
 )}
 </div>

 {/* Servicios elegibles para redención */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Servicios elegibles para canje gratuito
 </label>
 <p className="text-xs text-ink-subtle mb-2 text-ink-muted">
 Deja vacío para permitir todos los servicios. Si el cliente elige un servicio más caro, pagará la diferencia.
 </p>
 <div className="border border-hairline rounded-lg p-3 min-h-[100px] border-hairline">
 <p className="text-sm text-ink-subtle">
 Funcionalidad de selección de servicios (pendiente de implementar)
 </p>
 </div>
 </div>

 {/* Precio servicio básico */}
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-2">
 Precio servicio básico (para calcular diferencias)
 </label>
 <input
 type="number"
 value={config.basicServicePrice}
 onChange={(e) => updateConfig('basicServicePrice', parseFloat(e.target.value) || 0)}
 min="0"
 step="0.01"
 className="w-32 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle"
 />
 <span className="ml-2 text-sm text-ink-muted">€</span>
 </div>
 </div>
 </section>

 {/* Preview de valores - Show different content based on points system status */}
 <section className="border-t border-hairline pt-6 bg-surface-2 -mx-6 -mb-6 p-6 rounded-b-lg bg-surface-2 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">
 📊 Vista Previa de Valores
 </h3>

 {config.pointsSystemEnabled ? (
 <>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <p className="text-ink-muted">Puntos por servicio:</p>
 <p className="font-semibold">{config.pointsPerService.fixedValue || 0} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Puntos de bienvenida:</p>
 <p className="font-semibold text-success">+{config.welcomePoints} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Bonus primera visita:</p>
 <p className="font-semibold">+{config.firstVisitBonus} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Penalización no-show:</p>
 <p className="font-semibold text-danger">-{config.noShowPenalty} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Penalización cancelación:</p>
 <p className="font-semibold text-danger">-{config.lateCancelPenalty} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Umbral VIP:</p>
 <p className="font-semibold text-accent">{config.vipThreshold} puntos</p>
 </div>
 <div>
 <p className="text-ink-muted">Conversión puntos → sellos:</p>
 <p className="font-semibold">{config.pointsPerStamp} puntos = 1 sello</p>
 </div>
 <div>
 <p className="text-ink-muted">Tarjeta completa:</p>
 <p className="font-semibold">{config.stampsPerCard} sellos = {config.rewardDiscountType === 'percentage' ? `${config.rewardPercentage}% dcto` : `${config.rewardValueEuros}€ dcto`}</p>
 </div>
 <div>
 <p className="text-ink-muted">Bloqueo de cuenta:</p>
 <p className="font-semibold text-danger">{config.blockThreshold} puntos</p>
 </div>
 </div>

 {/* Example journey */}
 <div className="mt-6 p-4 bg-white rounded-lg border border-hairline bg-surface-2 border-hairline">
 <p className="text-xs font-semibold text-ink-muted mb-2">
 📖 Ejemplo de flujo del cliente:
 </p>
 <ol className="text-xs text-ink-muted space-y-1 ml-4 list-decimal text-ink-muted">
 <li>
 Cliente nuevo se registra → recibe <strong className="text-success">+{config.welcomePoints}</strong> puntos de bienvenida
 </li>
 <li>
 Primera visita → bonus adicional de <strong className="text-success">+{config.firstVisitBonus}</strong> puntos
 </li>
 <li>
 Completa servicio → gana <strong className="text-success">+{config.pointsPerService.fixedValue}</strong> puntos + 1 sello
 </li>
 <li>
 Después de {Math.ceil(config.vipThreshold / (config.pointsPerService.fixedValue || 10))} servicios → alcanza {config.vipThreshold} puntos → elegible para VIP
 </li>
 <li>
 Puede canjear {config.pointsPerStamp} puntos → 1 sello adicional
 </li>
 <li>
 Completa {config.stampsPerCard} sellos → canjea por {config.rewardDiscountType === 'percentage' ? `${config.rewardPercentage}% de descuento` : `${config.rewardValueEuros}€ de descuento`}
 </li>
 <li>
 Si no se presenta: <strong className="text-danger">-{config.noShowPenalty}</strong> puntos
 </li>
 <li>
 Si llega a {config.blockThreshold} puntos → cuenta bloqueada
 </li>
 </ol>
 </div>
 </>
 ) : (
 <>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <p className="text-ink-muted">Tarjeta completa:</p>
 <p className="font-semibold">{config.stampsPerCard} sellos = {config.rewardDiscountType === 'percentage' ? `${config.rewardPercentage}% dcto` : `${config.rewardValueEuros}€ dcto`}</p>
 </div>
 <div>
 <p className="text-ink-muted">Tarjetas activas máx:</p>
 <p className="font-semibold">{config.maxActiveCards} tarjetas</p>
 </div>
 </div>

 {/* Simplified journey without points */}
 <div className="mt-6 p-4 bg-white rounded-lg border border-hairline bg-surface-2 border-hairline">
 <p className="text-xs font-semibold text-ink-muted mb-2">
 📖 Ejemplo de flujo del cliente (sin sistema de puntos):
 </p>
 <ol className="text-xs text-ink-muted space-y-1 ml-4 list-decimal text-ink-muted">
 <li>Cliente nuevo se registra</li>
 <li>Completa servicio → gana 1 sello</li>
 <li>Completa {config.stampsPerCard} sellos → canjea por {config.rewardDiscountType === 'percentage' ? `${config.rewardPercentage}% de descuento` : `${config.rewardValueEuros}€ de descuento`}</li>
 </ol>
 </div>

 <div className="mt-4 p-3 bg-accent/10 border border-accent/30 rounded-lg">
 <p className="text-xs text-accent">
 ⚠️ El sistema de puntos está desactivado. Los clientes solo acumularán sellos por servicio.
 </p>
 </div>
 </>
 )}
 </section>
 </div>

 {/* Actions */}
 <div className="border-t border-hairline px-6 py-4 flex items-center justify-between bg-surface-2 border-hairline">
 <div>
 {hasChanges && (
 <span className="text-sm text-accent">
 ⚠️ Tienes cambios sin guardar
 </span>
 )}
 </div>
 <div className="flex gap-3">
 <button
 onClick={loadConfig}
 disabled={!hasChanges || saving}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-white border border-hairline rounded-lg hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed bg-surface-2 border-hairline"
 >
 Descartar cambios
 </button>
 <button
 onClick={handleSave}
 disabled={!hasChanges || saving}
 className="px-6 py-2 text-sm font-medium text-on-accent bg-accent rounded-lg hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {saving ? 'Guardando...' : 'Guardar configuración'}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};
